import React, { useState } from 'react';
import { AlertTriangle, X, Flag } from 'lucide-react';

const SearchResultIssueReporter = ({ onReport }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState('');
  const [otherDescription, setOtherDescription] = useState('');

  const issueTypes = [
    { id: 'inappropriate', label: 'Inappropriate content' },
    { id: 'poor_quality', label: 'Poor image quality' },
    { id: 'broken_link', label: 'Broken or missing image' },
    { id: 'wrong_context', label: 'Image doesn\'t match search' },
    { id: 'other', label: 'Other' }
  ];

  const handleSubmit = () => {
    if (!selectedIssue) return;

    const reportData = {
      issueType: selectedIssue,
      description: selectedIssue === 'other' ? otherDescription : ''
    };

    // Call the onReport callback if provided
    if (onReport) {
      onReport(reportData);
    }

    // Reset form and close popup
    setSelectedIssue('');
    setOtherDescription('');
    setIsPopupOpen(false);
  };

  const handleCancel = () => {
    setSelectedIssue('');
    setOtherDescription('');
    setIsPopupOpen(false);
  };

  return (
    <>
      {/* Report Button */}
      <button
        onClick={() => setIsPopupOpen(true)}
        className="flex items-center p-2 text-sm text-white hover:text-red-400 rounded-md transition-colors duration-200"
      >
        <Flag className='w-6 h-6' />
      </button>

      {/* Popup Overlay */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-orange-500" size={20} />
                <h3 className="text-lg font-semibold">Report Image Issue</h3>
              </div>
              <button
                onClick={handleCancel}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Help us improve by reporting issues with this result.
              </p>

              {/* Issue Type Selection */}
              <div className="space-y-2 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What's the issue?
                </label>
                {issueTypes.map((issue) => (
                  <label key={issue.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="issueType"
                      value={issue.id}
                      checked={selectedIssue === issue.id}
                      onChange={(e) => setSelectedIssue(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{issue.label}</span>
                  </label>
                ))}
              </div>

              {/* Other Description Field */}
              {selectedIssue === 'other' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please describe the issue:
                  </label>
                  <textarea
                    value={otherDescription}
                    onChange={(e) => setOtherDescription(e.target.value)}
                    placeholder="Describe the issue with this image..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selectedIssue || (selectedIssue === 'other' && !otherDescription.trim())}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SearchResultIssueReporter;
